import React from 'react';
import { Tooltip } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import {
	ILocationDiagram,
	IShelfWithLocations,
} from '../../../../redux/api/inventory';
import {
	IShelfSlotGrid,
	ISlotGrid,
	locationCapacityLabel,
	occupancyColor,
	shelfOccupancyPercent,
} from './rackVisual.helpers';
import { WEIGHT_TYPES } from '../../constants/inventory';
import ShelfActions from './ShelfActions';

interface Props {
	grid: ISlotGrid;
	canWrite: boolean;
	onLocationClick: (location: ILocationDiagram, shelf: IShelfWithLocations) => void;
	onAddLocation: (shelf: IShelfWithLocations, suggestedCode?: string) => void;
	onEditShelf: (shelf: IShelfWithLocations) => void;
	onDeleteShelf: (shelf: IShelfWithLocations) => void;
	onPermissionDenied: () => void;
	/** Uppercased location code to highlight (from the rack nav-bar search). */
	highlightCode?: string;
}

/** Thin rule drawn at the top of each shelf group to delineate shelves. */
const GROUP_BORDER = '1px solid #bfbfbf';
const BOX_HEIGHT = 58;
/** Offset so scrolled-to anchors clear the sticky rack header. */
const SCROLL_MARGIN = 140;

/**
 * Renders an INDIVIDUAL_SLOTS rack as a matrix: row numbers down the side and,
 * for each shelf, its own set of equal-width columns. Each shelf keeps its own
 * matrix (columns/rows/cells) so codes never collide across shelves and slots
 * stay evenly sized regardless of how many are filled.
 */
const SlotGrid: React.FC<Props> = ({
	grid,
	canWrite,
	onLocationClick,
	onAddLocation,
	onEditShelf,
	onDeleteShelf,
	onPermissionDenied,
	highlightCode,
}) => {
	// Shelf header: name + locations/items + weight usage.
	const renderShelfMeta = (shelf: IShelfWithLocations) => {
		const locationsCount = shelf.locations.length;
		const items = shelf.locations.reduce(
			(acc, loc) => acc + (loc.number_of_items || 0),
			0,
		);
		return (
			<div style={{ lineHeight: 1.5 }}>
				<div style={{ fontWeight: 600, fontSize: 13, color: '#595959' }}>
					{shelf.name}
				</div>
				<div style={{ fontSize: 11, fontWeight: 400, color: '#999' }}>
					{locationsCount} locs · {items} items
				</div>
				{shelf.max_weight != null && (
					<div style={{ fontSize: 11, fontWeight: 400, color: '#999' }}>
						{Math.round(shelf.occupied_weight || 0)}/{shelf.max_weight} lb ·{' '}
						{shelfOccupancyPercent(shelf)}% full
					</div>
				)}
			</div>
		);
	};

	// Right-hand cell: shelf info + edit/delete actions.
	const shelfInfoCell = (shelf: IShelfWithLocations, rowSpan: number) => (
		<td
			rowSpan={rowSpan}
			style={{
				borderLeft: '1px solid #d9d9d9',
				borderTop: GROUP_BORDER,
				background: 'transparent',
				padding: '8px 12px',
				verticalAlign: 'middle',
				whiteSpace: 'nowrap',
			}}
			title={shelf.name}
		>
			<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
				{renderShelfMeta(shelf)}
				<ShelfActions
				shelf={shelf}
				canWrite={canWrite}
				onEdit={onEditShelf}
				onDelete={onDeleteShelf}
				onPermissionDenied={onPermissionDenied}
			/>
			</div>
		</td>
	);

	// `percent` overrides the location's own occupancy (used for shared-shelf
	// bands, whose occupancy is weight-based at the shelf level).
	const slotBox = (
		location: ILocationDiagram,
		shelf: IShelfWithLocations,
		percent?: number,
	) => {
		const pct = percent ?? location.occupancy_percent;
		// Shared-shelf locations have no per-cell capacity: their occupancy is the
		// shelf's (weight) and is identical for every cell on the shelf. Showing it
		// per cell reads as a per-cell figure, so we drop the cell % and surface it
		// once at the shelf level instead (see renderShelfMeta). PTB-5929.
		const isSharedShelf = WEIGHT_TYPES.includes(location.type);
		const isHighlighted =
			!!highlightCode && location.code.toUpperCase() === highlightCode;
		return (
			<Tooltip
				title={
					<div>
						<div>
							<strong>{location.code}</strong> · {shelf.name}
						</div>
						<div>Items: {location.number_of_items}</div>
						<div>
							{isSharedShelf ? 'Shelf occupancy (weight)' : 'Occupancy'}:{' '}
							{Math.round(pct)}%
						</div>
						{locationCapacityLabel(location) && (
							<div>{locationCapacityLabel(location)}</div>
						)}
					</div>
				}
			>
				<div
					data-slot-code={location.code.toUpperCase()}
					onClick={() => onLocationClick(location, shelf)}
					style={{
						flex: 1,
						minWidth: 70,
						height: BOX_HEIGHT,
						border: isHighlighted ? '2px solid #1677ff' : '1px solid #d9d9d9',
						boxShadow: isHighlighted ? '0 0 0 3px rgba(22,119,255,0.25)' : undefined,
						background: occupancyColor(pct),
						scrollMarginTop: SCROLL_MARGIN,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						cursor: 'pointer',
						lineHeight: 1.3,
					}}
				>
					<span style={{ fontWeight: 600, fontSize: 12 }}>{location.code}</span>
					<span style={{ color: '#555', fontSize: 11 }}>
						{location.number_of_items} items
						{isSharedShelf ? '' : ` · ${Math.round(pct)}%`}
					</span>
					{locationCapacityLabel(location) && (
						<span style={{ color: '#888', fontSize: 10 }}>
							{locationCapacityLabel(location)}
						</span>
					)}
				</div>
			</Tooltip>
		);
	};

	const emptyBox = (shelf: IShelfWithLocations, code: string) => (
		<div
			onClick={() => (canWrite ? onAddLocation(shelf, code) : onPermissionDenied())}
			style={{
				flex: 1,
				minWidth: 70,
				height: BOX_HEIGHT,
				border: '1px dashed #d9d9d9',
				background: '#fff',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				color: '#d9d9d9',
				cursor: 'pointer',
			}}
		>
			<PlusOutlined />
		</div>
	);

	const contentCell = (
		group: IShelfSlotGrid,
		row: number,
		isGroupStart: boolean,
	) => (
		<td
			style={{
				width: '100%', // take all remaining width → grid is responsive
				padding: 2,
				borderTop: isGroupStart ? GROUP_BORDER : '1px solid #f0f0f0',
			}}
		>
			<div style={{ display: 'flex', gap: 4 }}>
				{group.columns.map((col) => {
					const cell = group.cells[`${col}-${row}`];
					return (
						<React.Fragment key={col}>
							{cell
								? slotBox(cell.location, cell.shelf)
								: emptyBox(group.shelf, `${col}${row}`)}
						</React.Fragment>
					);
				})}
			</div>
		</td>
	);

	return (
		<div style={{ overflowX: 'auto' }}>
			<table style={{ borderCollapse: 'collapse', width: '100%' }}>
				<colgroup>
					<col style={{ width: 28 }} />
				</colgroup>
				<tbody>
					{/* One shelf at a time; each keeps its own isolated matrix. */}
					{grid.shelves.map((group) => {
						const hasExtras = group.extras.length > 0;
						const isEmpty = group.rows.length === 0 && !hasExtras;
						// Right-side info cell spans the extras band (if any) + grid rows.
						// An empty shelf still renders a single placeholder row.
						const infoRowSpan = isEmpty
							? 1
							: (hasExtras ? 1 : 0) + group.rows.length;

						// Empty shelf (no grid cells, no bands): keep it visible and
						// manageable with an "add location" affordance + the actions
						// cell, instead of dropping it from the diagram (PTB-5919).
						if (isEmpty) {
							return (
								<tr key={group.shelf.id}>
									<td style={{ borderTop: GROUP_BORDER }} />
									<td
										style={{
											width: '100%',
											padding: 2,
											borderTop: GROUP_BORDER,
										}}
									>
										<div
											onClick={() =>
												canWrite
													? onAddLocation(group.shelf)
													: onPermissionDenied()
											}
											style={{
												flex: 1,
												minHeight: BOX_HEIGHT,
												border: '1px dashed #d9d9d9',
												background: '#fff',
												display: 'flex',
												flexDirection: 'column',
												alignItems: 'center',
												justifyContent: 'center',
												color: '#999',
												cursor: 'pointer',
												gap: 2,
											}}
										>
											<span style={{ fontWeight: 600, color: '#595959' }}>
												{group.shelf.name}
											</span>
											<span style={{ fontSize: 12 }}>
												<PlusOutlined style={{ marginRight: 6 }} />
												Add locations
											</span>
										</div>
									</td>
									{shelfInfoCell(group.shelf, infoRowSpan)}
								</tr>
							);
						}

						return (
							<React.Fragment key={group.shelf.id}>
								{hasExtras && (
									<tr>
										<td style={{ borderTop: GROUP_BORDER }} />
										<td
											style={{
												width: '100%',
												padding: 2,
												borderTop: GROUP_BORDER,
											}}
										>
											<div style={{ display: 'flex', gap: 4 }}>
												{group.extras.map((location) => (
													<React.Fragment key={location.id}>
														{slotBox(
															location,
															group.shelf,
															shelfOccupancyPercent(group.shelf),
														)}
													</React.Fragment>
												))}
											</div>
										</td>
										{shelfInfoCell(group.shelf, infoRowSpan)}
									</tr>
								)}

								{group.rows.map((row, idx) => {
									const isGroupStart = idx === 0 && !hasExtras;
									return (
										<tr key={row}>
											<td
												style={{
													textAlign: 'right',
													paddingRight: 6,
													fontSize: 11,
													color: '#888',
													verticalAlign: 'middle',
													borderTop: isGroupStart
														? GROUP_BORDER
														: undefined,
												}}
											>
												{row}
											</td>
											{contentCell(group, row, isGroupStart)}
											{idx === 0 && !hasExtras &&
												shelfInfoCell(group.shelf, infoRowSpan)}
										</tr>
									);
								})}
							</React.Fragment>
						);
					})}
				</tbody>
			</table>
		</div>
	);
};

export default SlotGrid;
